import React, { useState } from 'react';
import { IData, IDataItem } from './CircularPlot';

interface IColorSegmentsProps {
  label: string,
  data: IData[];
  centerX: number;
  centerY: number;
  outerRadius: number;
  innerRadius: number;
  hoverThickness?: number;
  fillAnge?: number;
  rotationOffset?: number;
  segmentGap: number
}

export const ColorSegments: React.FC<IColorSegmentsProps> = ({
  label,
  data,
  centerX,
  centerY,
  outerRadius,
  innerRadius,
  hoverThickness = 10,
  fillAnge = 270,
  rotationOffset = 0,
  segmentGap = .1
}) => {
  const [hoveredSegment, setHoveredSegment] = useState<null | number>(null);

  const totalFillAngleRad = (fillAnge / 360) * 2 * Math.PI;
  const rotationOffsetRad = (rotationOffset / 360) * 2 * Math.PI;

  const combinedData: IDataItem[] = data.reduce<IDataItem[]>((acc, item) => {
    return acc.concat(item.data);
  }, []);

  // Adjusted angle step (without considering gap)
  const adjustedAngleStep = totalFillAngleRad / combinedData.length;

  let currentStartAngle = rotationOffsetRad;

  return (
    <>
      {combinedData.map((item, index) => {
        const isHovered = index === hoveredSegment;
        const adjustedOuterRadius = isHovered ? outerRadius + hoverThickness : outerRadius;
        const adjustedInnerRadius = isHovered ? innerRadius - hoverThickness / 2 : innerRadius;

        // Add segment gap only after every second segment
        const startAngle = currentStartAngle;
        const endAngle = startAngle + adjustedAngleStep;

        // Update start angle for the next segment, adding gap after every second segment
        currentStartAngle = endAngle + (index % 2 === 1 ? segmentGap : 0);

        // Coordinates for outer and inner arcs
        const x1Outer = centerX + adjustedOuterRadius * Math.cos(startAngle);
        const y1Outer = centerY + adjustedOuterRadius * Math.sin(startAngle);
        const x2Outer = centerX + adjustedOuterRadius * Math.cos(endAngle);
        const y2Outer = centerY + adjustedOuterRadius * Math.sin(endAngle);

        const x1Inner = centerX + adjustedInnerRadius * Math.cos(startAngle);
        const y1Inner = centerY + adjustedInnerRadius * Math.sin(startAngle);
        const x2Inner = centerX + adjustedInnerRadius * Math.cos(endAngle);
        const y2Inner = centerY + adjustedInnerRadius * Math.sin(endAngle);

        const largeArcFlag = adjustedAngleStep > Math.PI ? 1 : 0;

        return (
          <path
            key={index}
            d={`
              M ${x1Inner},${y1Inner}
              L ${x1Outer},${y1Outer}
              A ${adjustedOuterRadius},${adjustedOuterRadius} 0 ${largeArcFlag},1 ${x2Outer},${y2Outer}
              L ${x2Inner},${y2Inner}
              A ${adjustedInnerRadius},${adjustedInnerRadius} 0 ${largeArcFlag},0 ${x1Inner},${y1Inner}
              Z
            `}
            className="circular-plot-segment"
            fill={item.color}
            stroke="#fff"
            strokeWidth="0"
            strokeLinejoin="round"
            onMouseEnter={() => setHoveredSegment(index)}
            onMouseLeave={() => setHoveredSegment(null)}
          />
        );
      })}
    </>
  );
};
